import  { Utils } from '../../Utils';
import EventDispatcher from '../../events/EventDispatcher';

import {CACHE} from '../../globals';
import * as FEATURES from '../../layout/features';

window.AudioContext = window.AudioContext || window.webkitAudioContext;

function dummyErrorHandler() {}

function getAudioTime(context) {
    return context ? context.currentTime : (new Date).getTime();
}

// hack for iOS to unlock sound for current session
if (FEATURES.IOS && FEATURES.WEBAUDIO_SUPPORT) {
    (function (evt) {
        let context = new window.AudioContext();

        function unlockIOSSound() {
            window.removeEventListener(evt, unlockIOSSound);
            // play empty sound after user action to unlock sounds for fuckn smart-smart-smart iOS
            var sound = context.createBufferSource();
            sound.buffer = context.createBuffer(1, 1, 22050);
            sound.connect(context.destination);
            sound.noteOn ? sound.noteOn(0) : sound.start(0);
        }

        window.addEventListener(evt, unlockIOSSound);
    })(FEATURES.TOUCH_EVENTS.START);
}

class ChannelPlaybackStatus {

    constructor(context = null, duration = 0) {
        this.context = context;
        this.duration = duration;
        this.started = NaN;
        this.durationPlayed = 0;
    }

    destructor() {
        this.context = null;
    }

    reset() {
        this.started = NaN;
        this.durationPlayed = 0;
    }

    play() {
        this.started = getAudioTime(this.context);
    }

    stop() {
        this.reset();
    }

    pause() {
        if (this.isPlaying) {
            this.durationPlayed += getAudioTime(this.context) - this.started;
        }
        this.started = NaN;
    }

    resume() {
        if (!this.isPlaying) {
            this.play();
        }
    }

    get currentPosition() {
        let pos = 0;
        if (this.isPlaying && this.duration > 0) {
            pos += getAudioTime(this.context) - this.started;
            while (pos > this.duration) {
                pos -= this.duration;
            }
        }
        return Math.max(0, pos);
    }

    get timeLeft() {
        return this.duration - this.currentPosition;
    }

    get isPlaying() {
        return !isNaN(this.started);
    }
}

/**
 * @class
 * @description Аудио-канал
 * @memberof module:media.module:audio
 */
class AudioChannel extends EventDispatcher {

    static create(...args) {
        return FEATURES.WEBAUDIO_SUPPORT
            ? new WAAudioChannel(...args)
            : new HTMLAudioChannel(...args);
    }

    constructor(context = null) {
        super();

        this.context = context;

        /** @ignore */
        this.playbackStatus = new ChannelPlaybackStatus(context);

        /**
         * Do not play sounds in this channel
         * @type {boolean}
         */
        this.disabled = false;

        /**
         * Exclude this channel from free channel seek
         * @type {boolean}
         */
        this.locked = false;

        /**
         * Wrapper depends on browser features (tag <audio> or AudioBufferSourceNode)
         * @type {HTMLElement|AudioBufferSourceNode}
         */
        this.audioWrapper = null;

        this.startPlayTime = 0;
    }

    destructor() {
        this.stop();
        this.playbackStatus.destructor();
        this.context = null;
        this.playbackStatus = null;
        this.audioWrapper = null;
        this.disabled = true;
    }

    controlPlay() {
        this.playbackStatus.reset();
        if (this.audioWrapper) {
            if (!this.audioWrapper.doLoop) {
                /**
                 * Окончание проигрывания звука
                 * @event module:media.module:audio.AudioChannel#end
                 */
                this.emit('end');
                // this.audioWrapper = null;
            }
            else {
                this.playbackStatus.play(); // looped play
            }
        }
    }

    get busy() {
        return this.audioWrapper && this.playbackStatus.isPlaying;
    }

    createNewAudio() {
        this.stop();
        this.audioWrapper = null;
        return !this.disabled;
    }

    play() {
        this.playbackStatus.reset();
        if (this.audioWrapper && !this.disabled) {
            this.playbackStatus.play();
            return true;
        }
        return false;
    }

    stop() {
        if (this.playbackStatus.isPlaying) {
            this.playbackStatus.reset();
            return !!this.audioWrapper;
        }
        return false;
    }

    pause() {
        if (this.playbackStatus.isPlaying) {
            this.playbackStatus.pause();
            return !!this.audioWrapper;
        }
        return false;
    }

    resume() {
        if (this.audioWrapper && !this.playbackStatus.isPlaying && !this.disabled) {
            this.playbackStatus.resume();
            return true;
        }
        return false;
    }
}

class HTMLAudioChannel extends AudioChannel {

    constructor() {
        super();

        this.controlPlay = Utils.proxy(this.controlPlay, this);
    }

    destructor() {
        super.destructor();
    }

    controlPlay() {
        super.controlPlay();
        if (this.audioWrapper && this.audioWrapper.doLoop) {
            if (FEATURES.FIREFOX) {
                this.play(this.audioWrapper.src, true);
            }
            else {
                this.audioWrapper.currentTime = 0;
                this.audioWrapper.play();
            }
        }
    }

    /** @ignore */
    createNewAudio(url, loop = false) {

        if (!super.createNewAudio(url, loop)) {
            return false;
        }

        if(this.audioWrapper) {
            this.audioWrapper.removeEventListener("ended", this.controlPlay, false);
        }

        let cached = CACHE.get(url);

        if (!cached) {
            throw Error(`Playing sound without preloading "${url}"`);
        }

        this.audioWrapper = cached.cloneNode(true);
        this.audioWrapper.loop = false;
        this.audioWrapper.doLoop = !!loop;

        this.playbackStatus.duration = this.duration;

        this.audioWrapper.addEventListener("ended", this.controlPlay, false);

        return this.audioWrapper;
    }

    /** @ignore */
    play(url, loop = false) {
        if (this.createNewAudio(url, loop) && super.play(url, loop)) {
            this.playWrapper();
        }
    }

    playWrapper() {
        if(!this.audioWrapper) return;
        var p = this.audioWrapper.play();
        if (p && (typeof window.Promise !== 'undefined') && (p instanceof Promise)) {
            p.catch(dummyErrorHandler);
        }
    }

    /**
     * Остановка проигрывания
     */
    stop() {
        if (super.stop()) {
            try {
                this.audioWrapper.pause();
                this.audioWrapper.currentTime = 0.0;
                this.audioWrapper = null;
                return true;
            }
            catch(e){
                return false;
            }
        }
    }

    /**
     * Пауза проигрывания
     */
    pause() {
        if (super.pause()) {
            try {
                this.audioWrapper.pause();
            }
            catch(e){}
        }
    }

    /**
     * Возобновление проигрывания
     */
    resume() {
        if (super.resume()) {
            this.playWrapper();
        }
    }

    /**
     * Получение текущей позиции проигрывания
     */
    get position() {
        return this.audioWrapper && this.audioWrapper.currentTime * 1000 || 0;
    }

    /**
     * Получение общей длительности звука
     */
    get duration() {
        return this.audioWrapper && this.audioWrapper.duration * 1000 || 0;
    }

    /**
     * Получение громкости канала
     * @returns Number
     */
    get volume() {
        return this.audioWrapper && this.audioWrapper.volume || 0;
    }

    /**
     * Установка громкости канала
     * @param {Number} val - от 0 до 1
     */
    set volume(val) {
        if (this.audioWrapper) {
            this.audioWrapper.volume = Math.max(0, Math.min(1, val));
        }
    }
}

class WAAudioChannel extends AudioChannel {

    constructor(context, destination = context.destination) {
        super();
        this.context = context;
        this.destination = destination;

        this.gainNode = this.context.createGainNode ? this.context.createGainNode() : this.context.createGain();
        this.gainNode.connect(this.destination);

        this.controlPlay = Utils.proxy(this.controlPlay, this);

        this.waCheckInterval = null;
    }

    destructor() {
        if (this.waCheckInterval) {
            clearInterval(this.waCheckInterval);
        }
        if (this.gainNode) {
            this.gainNode.disconnect();
        }
        if (this.audioWrapper) {
            this.audioWrapper.onended = null;
            this.audioWrapper.disconnect();
        }
        this.waCheckInterval = null;
        this.audioWrapper = null;
        this.gainNode = null;
        this.context = null;
        this.destination = null;
        super.destructor();
    }

    controlPlay() {
        super.controlPlay();
        if (!this.audioWrapper && this.waCheckInterval) {
            clearInterval(this.waCheckInterval);
            this.waCheckInterval = null;
        }
    }

    /** @ignore */
    createNewAudio(url, loop = false) {

        if (this.audioWrapper) {
            try {
                this.audioWrapper.disconnect();
                this.audioWrapper.onended = null;
            }
            catch(e){}
        }

        if (!super.createNewAudio()) return false;
        if (!this.context) return false;

        this.audioWrapper = this.context.createBufferSource();
        this.audioWrapper.doLoop = !!loop;
        this.audioWrapper.loop = !!loop;
        this.audioWrapper.connect(this.gainNode);
        this.audioWrapper.buffer = CACHE.get(url);

        this.playbackStatus.duration = this.duration;

        return this.audioWrapper;
    }

    /** @ignore */
    play(url, loop = false) {
        if (this.createNewAudio(url, loop) && super.play(url, loop)) {
            (this.audioWrapper.noteOn ? this.audioWrapper.noteOn(0) : this.audioWrapper.start(0));

            if (typeof this.audioWrapper.playbackState != "undefined") {
                this.waCheckInterval = setInterval(() => {
                    if (!this.audioWrapper || (this.audioWrapper.playbackState == this.audioWrapper.FINISHED_STATE)) {
                        this.controlPlay();
                    }
                }, 100);
            }
            else {
                this.audioWrapper.onended = this.controlPlay;
            }

        }
    }

    /**
     * Остановка проигрывания
     */
    stop() {
        if (super.stop()) {
            try {
                (this.audioWrapper.noteOff ? this.audioWrapper.noteOff(0) : this.audioWrapper.stop(0));
                return true;
            }
            catch(e) {
                return false;
            }
        }
    }

    /**
     * Пауза проигрывания
     */
    pause() {
        if (super.pause()) {
            try {
                this.audioWrapper.disconnect();
            }
            catch(e) {}
        }
    }

    /**
     * Возобновление проигрывания
     */
    resume() {
        if (super.resume()) {
            try {
                this.audioWrapper.connect(this.destination);
            }
            catch (e) {}
        }
    }

    /**
     * Получение текущей позиции проигрывания
     * @readonly
     */
    get position() {
        return this.playbackStatus.currentPosition;
    }

    /**
     * Получение общей длительности звука
     * @readonly
     */
    get duration() {
        return this.audioWrapper && this.audioWrapper.buffer && this.audioWrapper.buffer.duration || 0;
    }

    /**
     * Получение громкости канала
     * @returns Number
     */
    get volume() {
        return this.gainNode.gain.value;
    }

    /**
     * Установка громкости канала
     * @param {Number} val - от 0 до 1
     */
    set volume(val) {
        this.gainNode.gain.value = Math.max(0, Math.min(1, val));
    }
}

export default AudioChannel;